/** Non-secret tab preferences. A document keeps its original transport identity. */
import {
  type SandboxCohort,
  sandboxCohortKey,
  type SandboxSelection,
  SandboxSelectionSchema,
} from '@shared/sandbox';
const SELECTION_KEY = 'deepthink.sandbox.selection';
const COHORTS_KEY = 'deepthink.sandbox.cohorts';
function readContext(): {
  selection: SandboxSelection | null;
  invalid: boolean;
} {
  if (typeof window === 'undefined') return { selection: null, invalid: false };
  try {
    const stored = sessionStorage.getItem(SELECTION_KEY);
    if (stored === null) return { selection: null, invalid: false };
    return {
      selection: SandboxSelectionSchema.parse(JSON.parse(stored)),
      invalid: false,
    };
  } catch {
    // An unreadable context must never silently turn a sample write into an admin write.
    return { selection: null, invalid: true };
  }
}
export function readSandboxSelection(): SandboxSelection | null {
  return readContext().selection;
}
// Immutable until navigation: unload writes and retries belong to the old page.
const documentContext = readContext();
export const documentSandboxSelection = documentContext.selection;
export const documentSandboxInvalid = documentContext.invalid;
export function selectSandbox(
  selection: SandboxSelection,
  cohort: SandboxCohort
): void {
  const checked = SandboxSelectionSchema.parse(selection);
  sessionStorage.setItem(SELECTION_KEY, JSON.stringify(checked));
  let cohorts: Record<string, string> = {};
  try {
    cohorts = JSON.parse(sessionStorage.getItem(COHORTS_KEY) ?? '{}');
  } catch {
    /* no saved samples */
  }
  sessionStorage.setItem(
    COHORTS_KEY,
    JSON.stringify({
      ...cohorts,
      [sandboxCohortKey(cohort)]: checked.workspaceId,
    })
  );
}
export function lastSandboxForCohort(
  cohort: SandboxCohort
): string | undefined {
  try {
    const id = JSON.parse(sessionStorage.getItem(COHORTS_KEY) ?? '{}')[
      sandboxCohortKey(cohort)
    ];
    return typeof id === 'string' && /^[a-f\d]{24}$/i.test(id) ? id : undefined;
  } catch {
    return undefined;
  }
}
export function clearSandboxSelection(): void {
  sessionStorage.removeItem(SELECTION_KEY);
}
export function sandboxEndpoint(
  endpoint: string,
  selection = documentSandboxSelection
): string {
  if (/^\/users\/me\/sandbox-view(?:\?|$)/.test(endpoint)) return endpoint;
  if (documentSandboxInvalid)
    throw new Error(
      'Invalid sample selection. Use Back to super admin to exit.'
    );
  if (!selection) return endpoint;
  if (
    !endpoint.startsWith('/') ||
    endpoint.startsWith('//') ||
    endpoint.startsWith('/sandbox/')
  )
    throw new Error('Invalid sample API endpoint');
  return `/sandbox/${selection.workspaceId}/${selection.view}${endpoint}`;
}
export function sandboxStorageKey(key: string): string {
  return documentSandboxSelection
    ? `${key}:sandbox:${documentSandboxSelection.workspaceId}:${documentSandboxSelection.view}`
    : documentSandboxInvalid
      ? `${key}:sandbox:invalid`
      : key;
}
